import { Message } from '../types/ai';
import { User as UserType } from '../types/user';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Card } from './ui/card';
import { User, Bot, Trash2, RotateCcw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FunctionCallIndicator } from './FunctionCallIndicator';
import { MessageAttachments } from './MessageAttachments';
import { FileDownloadCard } from './FileDownloadCard';
import { ToolCallsList, ToolCallDisplay } from './ToolCallDisplay';
import { conversationService } from '../services/supabase-service';
import { useState } from 'react';
import { useToast } from '../hooks/use-toast';

interface ChatMessageProps {
  message: Message;
  aiLogo: string;
  aiColor: string;
  providerId?: string;
  functionCall?: {
    name: string;
    isExecuting: boolean;
    result?: any;
  };
  generatedFiles?: Array<{
    filename: string;
    content: string;
    contentType: string;
    size: number;
  }>;
  currentUser?: UserType; // Usuário atual (para verificar se é super admin)
  onMessageDeleted?: () => void; // Callback após exclusão
  messageType?: 'regular' | 'assistant'; // Tipo de mensagem
}

export function ChatMessage({ 
  message, 
  aiLogo, 
  aiColor, 
  providerId, 
  functionCall, 
  generatedFiles,
  currentUser,
  onMessageDeleted,
  messageType = 'regular'
}: ChatMessageProps) {
  const isUser = message.role === 'user';
  // Detectar tool message por messageType OU por role='system' com toolId OU por conteúdo
  const isToolMessage = message.messageType === 'tool' || 
    (message.role === 'system' && message.toolId) ||
    (message.role === 'system' && (message.content.startsWith('Executado:') || message.content.startsWith('Falhou:')));
  const isSuperAdmin = currentUser?.role === 'superadmin';
  const isDeleted = !!message.deleted_at;
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  // Se a mensagem está deletada e o usuário NÃO é super admin, não mostrar
  if (isDeleted && !isSuperAdmin) {
    return null;
  }
  
  // 🔧 Renderização para mensagens de TOOL — usar o mesmo ToolCallDisplay definitivo (abas Request | Response | Cálculos)
  if (isToolMessage) {
    const toolCallAsResult = {
      toolId: message.toolId || 'unknown',
      operationId: message.toolOperation || message.toolName || 'unknown',
      operationName: message.toolName || message.toolOperation || message.content?.replace(/^(Executado|Falhou):\s*/, '') || 'unknown',
      parameters: message.toolParameters ?? {},
      result: message.toolResult,
      error: message.toolStatus === 'failed' ? (message.toolResult?.error ?? message.content) : undefined,
      status: (message.toolStatus || 'completed') as 'pending' | 'running' | 'completed' | 'failed',
      httpStatus: (message as any).httpStatus as number | undefined,
      startedAt: message.timestamp || new Date(),
      completedAt: message.timestamp || new Date()
    };
    return (
      <div className="flex justify-start">
        <ToolCallDisplay toolCall={toolCallAsResult} />
      </div>
    );
  }

  // Função para excluir mensagem (soft delete)
  const handleDeleteMessage = async () => {
    if (!currentUser || !isSuperAdmin) return;
    
    const confirmDelete = window.confirm('Tem certeza que deseja excluir esta mensagem?\n\nA mensagem será marcada como excluída mas não será removida permanentemente.');
    
    if (!confirmDelete) return;

    setIsDeleting(true);
    try {
      await conversationService.softDeleteMessage(message.id, currentUser.id, messageType);
      toast.success('Mensagem excluída com sucesso');
      if (onMessageDeleted) onMessageDeleted();
    } catch (error) {
      console.error('Erro ao excluir mensagem:', error);
      toast.error('Erro ao excluir mensagem');
    } finally {
      setIsDeleting(false);
    }
  };

  // Função para restaurar mensagem
  const handleRestoreMessage = async () => {
    if (!currentUser || !isSuperAdmin) return;
    
    setIsDeleting(true);
    try {
      await conversationService.restoreDeletedMessage(message.id, currentUser.id, messageType);
      toast.success('Mensagem restaurada com sucesso');
      if (onMessageDeleted) onMessageDeleted();
    } catch (error) {
      console.error('Erro ao restaurar mensagem:', error);
      toast.error('Erro ao restaurar mensagem');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Função para renderizar a logo PNG baseada no providerId
  const renderAILogo = () => {
    if (!providerId) return aiLogo;
    
    switch (providerId) {
      case 'openai':
        return (
          <img 
            src="/gpt-logo.png?v=1" 
            alt="GPT Logo" 
            className="w-4 h-4 object-contain" 
            style={{ maxWidth: '16px', maxHeight: '16px' }}
          />
        );
      case 'anthropic':
        return (
          <img 
            src="/claude-logo.png?v=1" 
            alt="Claude Logo" 
            className="w-4 h-4 object-contain"
            style={{ maxWidth: '16px', maxHeight: '16px' }}
          />
        );
      case 'google':
        return (
          <img 
            src="/gemini-logo.png?v=1" 
            alt="Gemini Logo" 
            className="w-4 h-4 object-contain"
            style={{ maxWidth: '16px', maxHeight: '16px' }}
          />
        );
      case 'perplexity':
        return (
          <img 
            src="/perplexity-logo.png?v=1" 
            alt="Perplexity Logo" 
            className="w-4 h-4 object-contain"
            style={{ maxWidth: '16px', maxHeight: '16px' }}
          />
        );
      default:
        return aiLogo;
    }
  };
  
  // Definir espaçamento baseado no tipo de mensagem
  const getSpacingClass = () => {
    if (isUser) return 'mt-8'; // Mensagem do usuário tem bastante espaço acima
    if (isToolMessage) return 'mt-0.5'; // Tools ficam bem próximas entre si
    // Resposta final da IA - espaço mínimo acima (logo após as tools)
    return 'mt-1';
  };

  return (
    <div className={getSpacingClass()}>
      {/* Indicador de função sendo executada */}
      {functionCall && !isUser && (
        <FunctionCallIndicator
          functionName={functionCall.name}
          isExecuting={functionCall.isExecuting}
          result={functionCall.result}
        />
      )}
      
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} relative group`}>
        {/* Mensagem do usuário - Clean sem avatar, nome ou horário */}
        {isUser ? (
          <div className="max-w-[70%] relative">
            {/* Botões de ação do super admin */}
            {isSuperAdmin && (
              <div className="absolute -left-10 top-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                {!isDeleted ? (
                  <button
                    onClick={handleDeleteMessage}
                    disabled={isDeleting}
                    className="p-1.5 hover:bg-red-100 rounded text-red-600 hover:text-red-700 transition-colors"
                    title="Excluir mensagem"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleRestoreMessage}
                    disabled={isDeleting}
                    className="p-1.5 hover:bg-green-100 rounded text-green-600 hover:text-green-700 transition-colors"
                    title="Restaurar mensagem"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            <Card className={`px-3 py-2 bg-primary text-primary-foreground ${isDeleted ? 'opacity-60 border-2 border-red-400' : ''}`}>
              {/* Aviso de mensagem deletada (apenas super admin vê) */}
              {isDeleted && isSuperAdmin && (
                <div className="mb-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded flex items-center gap-1">
                  <Trash2 className="h-3 w-3" />
                  <span>Excluída por {message.deletedByName || 'Super Admin'}</span>
                </div>
              )}

              <div className={`text-sm prose prose-sm max-w-none ${isDeleted ? 'line-through' : ''}`}>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="text-sm">{children}</li>,
                  code: ({ children, className }) => {
                    const isInline = !className;
                    if (isInline) {
                      return <code className="bg-white/20 px-1 py-0.5 rounded text-xs font-mono">{children}</code>;
                    }
                    return (
                      <pre className="bg-white/20 p-2 rounded text-xs font-mono overflow-x-auto">
                        <code>{children}</code>
                      </pre>
                    );
                  },
                  pre: ({ children }) => <div className="mb-2">{children}</div>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-white/30 pl-4 italic opacity-80 mb-2">
                      {children}
                    </blockquote>
                  ),
                  h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  a: ({ children, href }) => (
                    <a href={href} className="text-white underline hover:no-underline" target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                  img: ({ src, alt }) => (
                    <img 
                      src={src} 
                      alt={alt || 'Imagem gerada'} 
                      className="max-w-full h-auto rounded-lg shadow-lg my-4 border"
                      style={{ maxHeight: '400px' }}
                      onError={(e) => {
                        console.log('Erro ao carregar imagem:', src);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={() => console.log('Imagem carregada com sucesso:', src)}
                    />
                  )
                }}
              >
                {message.content}
              </ReactMarkdown>
              
              {/* Anexos da mensagem do usuário */}
              {message.attachments && message.attachments.length > 0 && (
                <MessageAttachments attachments={message.attachments} />
              )}
            </div>
          </Card>
          </div>
        ) : (
          /* Mensagem da IA - Logo na esquerda, texto na direita (mesma linha das tools) */
          <div className="flex items-start gap-3 max-w-[85%] relative">
            {/* Logo da IA na coluna do avatar */}
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <div className="w-4 h-4">
                {renderAILogo()}
              </div>
            </div>
            
            {/* Conteúdo da mensagem */}
            <div className="flex-1">
              {/* Botões de ação do super admin */}
              {isSuperAdmin && (
                <div className="absolute -right-10 top-0 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-1">
                  {!isDeleted ? (
                    <button
                      onClick={handleDeleteMessage}
                      disabled={isDeleting}
                      className="p-1.5 hover:bg-red-100 rounded text-red-600 hover:text-red-700 transition-colors"
                      title="Excluir mensagem"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleRestoreMessage}
                      disabled={isDeleting}
                      className="p-1.5 hover:bg-green-100 rounded text-green-600 hover:text-green-700 transition-colors"
                      title="Restaurar mensagem"
                    >
                      <RotateCcw className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
              
              {/* Aviso de mensagem deletada (apenas super admin vê) */}
              {isDeleted && isSuperAdmin && (
                <div className="text-xs bg-red-50 text-red-800 px-3 py-1.5 rounded flex items-center gap-1 border border-red-200">
                  <Trash2 className="h-3 w-3" />
                  <span>Excluída por {message.deletedByName || 'Super Admin'}</span>
                </div>
              )}
              
              {/* Chamadas de ferramentas - ACIMA da resposta */}
              {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
                <ToolCallsList toolCalls={message.toolCalls} />
              )}
              
              {/* Conteúdo da mensagem sem card de fundo */}
              <div className={`text-sm prose prose-sm max-w-none text-gray-900 ${isDeleted ? 'opacity-50 line-through' : ''}`}>
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                  li: ({ children }) => <li className="text-sm">{children}</li>,
                  code: ({ children, className }) => {
                    const isInline = !className;
                    if (isInline) {
                      return <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">{children}</code>;
                    }
                    return (
                      <pre className="bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto">
                        <code>{children}</code>
                      </pre>
                    );
                  },
                  pre: ({ children }) => <div className="mb-2">{children}</div>,
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 mb-2">
                      {children}
                    </blockquote>
                  ),
                  h1: ({ children }) => <h1 className="text-lg font-bold mb-2">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-base font-bold mb-2">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-sm font-bold mb-2">{children}</h3>,
                  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                  em: ({ children }) => <em className="italic">{children}</em>,
                  a: ({ children, href }) => (
                    <a href={href} className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  ),
                  img: ({ src, alt }) => (
                    <img 
                      src={src} 
                      alt={alt || 'Imagem gerada'} 
                      className="max-w-full h-auto rounded-lg shadow-lg my-4 border"
                      style={{ maxHeight: '400px' }}
                      onError={(e) => {
                        console.log('Erro ao carregar imagem:', src);
                        e.currentTarget.style.display = 'none';
                      }}
                      onLoad={() => console.log('Imagem carregada com sucesso:', src)}
                    />
                  )
                }}
              >
                {message.content}
              </ReactMarkdown>
              
              {/* Anexos da mensagem da IA */}
              {message.attachments && message.attachments.length > 0 && (
                <MessageAttachments attachments={message.attachments} />
              )}
              
              {/* Arquivos gerados pela IA */}
              {!isUser && generatedFiles && generatedFiles.length > 0 && (
                <FileDownloadCard files={generatedFiles} />
              )}
            </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}